import type { TicketEventEntry } from '@easyhelp/shared';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId } from 'react';
import { groupInterval } from './thread-groups';
import { ThreadEvent } from './ThreadEvent';

/** Filo sottile ai lati della riga del gruppo: decorativo, nessun ruolo. */
const RULE_SX = { flex: 1, borderTop: 1, borderColor: 'divider', minWidth: 16 } as const;

/**
 * Gruppo di eventi di sistema consecutivi (#120): una riga compatta e centrata, «6 eventi ·
 * 16:14–16:18 › mostra», che al clic si espande al suo posto; «nascondi» la richiude. La riga è un
 * pulsante con `aria-expanded`, e il nome dice quanti eventi contiene e in che intervallo.
 */
export function ThreadEventGroup({
  events,
  open,
  onToggle,
}: {
  events: readonly TicketEventEntry[];
  open: boolean;
  onToggle: () => void;
}) {
  const listId = useId();
  const interval = groupInterval(events);
  const action = open ? 'nascondi' : 'mostra';
  return (
    <Box component="li" data-variant="gruppo" sx={{ listStyle: 'none' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={RULE_SX} />
        <ButtonBase
          // Il nome contiene le parole a vista («6 eventi», le ore, «mostra») e legge l'intervallo
          // per esteso: «6 eventi dalle 16:14 alle 16:18, mostra».
          aria-label={`${events.length} eventi ${interval.spoken}, ${action}`}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={onToggle}
          sx={{
            px: 2,
            py: 0.5,
            borderRadius: 1,
            color: 'text.secondary',
            typography: 'caption',
            textAlign: 'center',
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 },
          }}
        >
          {`${events.length} eventi · ${interval.visible}`}&nbsp;
          <ChevronRightIcon
            aria-hidden="true"
            sx={{
              fontSize: 16,
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 150ms ease-out',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
          <Typography component="span" variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
            {action}
          </Typography>
        </ButtonBase>
        <Box sx={RULE_SX} />
      </Stack>
      {open && (
        <Stack id={listId} component="ol" spacing={0.5} sx={{ m: 0, mt: 1, p: 0 }}>
          {events.map((event) => (
            <ThreadEvent key={event.id} entry={event} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
